import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import type { ContainerGroup } from './ProcurementContainers';
import CustomsDocumentsPanel from './CustomsDocumentsPanel';
import CustomsPaymentsPanel from './CustomsPaymentsPanel';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ProcurementDocType = 'bill_of_entry' | 'tax_bill' | 'levy' | 'other';

export interface BoeDocument {
    id: number;
    boe_id: number;
    doc_type: ProcurementDocType;
    file_url: string;
    original_filename: string | null;
}

export interface BoeItem {
    id: number;
    boe_id: number;
    vehicle_id: number | null;
    vin: string;
    item_no: string | null;
    description: string | null;
    tax_amount: number;
}

export interface BoeTransactionSummary {
    amount: number;
    fee: number | null;
    currency: string;
    transaction_at: string;
    description: string | null;
}

/** A container's Bill of Entry — the persisted container entity — plus its uploaded documents and
 * the (matched-to-current-membership) per-vehicle tax items. Queried by `useContainerBoe`, keyed by
 * sorted member vehicle ids (never the derived cluster key — see `ContainerGroup.fullMembers`). */
export interface ContainerBoe {
    id: number;
    boe_no: string;
    bl_no: string;
    boe_date: string;
    office_code: string | null;
    reference_no: string | null;
    total_amount: number;
    line_items_total: number | null;
    currency: string;
    vehicle_count: number | null;
    tax_transaction_id: number | null;
    port_charges_transaction_id: number | null;
    levy_transaction_id: number | null;
    procurement_documents: BoeDocument[];
    tax_transaction: BoeTransactionSummary | null;
    port_charges_transaction: BoeTransactionSummary | null;
    levy_transaction: BoeTransactionSummary | null;
    items: BoeItem[];
}

// ─── Data hook ──────────────────────────────────────────────────────────────

/**
 * Fetches the container's Bill of Entry (if any) keyed by sorted member vehicle ids — never the
 * derived cluster key, since a BL backfill (confirming a parsed BOE) re-clusters tiles while vehicle
 * ids stay stable. Returns null when no BOE has been recorded for this container yet (gates the
 * Payments tab).
 *
 * Two-step fetch, deliberately: step 1 only asks "which BOE (if any) do these members belong to,"
 * then step 2 fetches that BOE's items WITHOUT filtering by `memberIds` again. The container tile's
 * membership snapshot (`memberIds`) can go stale relative to the BOE's own item rows — e.g. the
 * cluster gets re-split after the BOE was recorded — and a single `.in('vehicle_id', memberIds)`
 * query would silently drop any BOE item outside that stale snapshot. The BOE's own item rows are
 * the ground truth for what it covers, not the tile.
 */
export function useContainerBoe(memberIds: number[], enabled: boolean) {
    return useQuery({
        queryKey: ['container-boe', memberIds],
        queryFn: async (): Promise<ContainerBoe | null> => {
            if (memberIds.length === 0) return null;

            // Step 1: find which BOE (if any) these members belong to. Every member of a container
            // shares the same boe_id once a BOE is recorded, so one hit is enough.
            const { data: linkRows, error: linkErr } = await supabase
                .from('bill_of_entry_items')
                .select('boe_id')
                .in('vehicle_id', memberIds)
                .limit(1);
            if (linkErr) throw linkErr;
            if (!linkRows || linkRows.length === 0) return null;
            const boeId = (linkRows[0] as any).boe_id as number;

            // Step 2: fetch the FULL BOE row by id, with ALL of its items — not re-filtered by
            // memberIds — so items outside the (possibly stale) tile snapshot survive.
            const { data: boeRow, error: boeErr } = await supabase
                .from('bills_of_entry')
                .select(`
                    id, boe_no, bl_no, boe_date, office_code, reference_no, total_amount,
                    line_items_total, currency, vehicle_count, tax_transaction_id, port_charges_transaction_id,
                    levy_transaction_id,
                    procurement_documents ( id, boe_id, doc_type, file_url, original_filename ),
                    tax_transaction:transactions!bills_of_entry_tax_transaction_id_fkey ( amount, fee, currency, transaction_at, description ),
                    port_charges_transaction:transactions!bills_of_entry_port_charges_transaction_id_fkey ( amount, fee, currency, transaction_at, description ),
                    levy_transaction:transactions!bills_of_entry_levy_transaction_id_fkey ( amount, fee, currency, transaction_at, description ),
                    bill_of_entry_items ( id, boe_id, vehicle_id, vin, item_no, description, tax_amount )
                `)
                .eq('id', boeId)
                .single();
            if (boeErr) throw boeErr;
            if (!boeRow) return null;
            const b = boeRow as any;

            const items: BoeItem[] = (b.bill_of_entry_items ?? []).map((row: any) => ({
                id: row.id,
                boe_id: row.boe_id,
                vehicle_id: row.vehicle_id,
                vin: row.vin,
                item_no: row.item_no,
                description: row.description,
                tax_amount: row.tax_amount,
            }));

            return {
                id: b.id,
                boe_no: b.boe_no,
                bl_no: b.bl_no,
                boe_date: b.boe_date,
                office_code: b.office_code,
                reference_no: b.reference_no,
                total_amount: b.total_amount,
                line_items_total: b.line_items_total,
                currency: b.currency,
                vehicle_count: b.vehicle_count,
                tax_transaction_id: b.tax_transaction_id,
                port_charges_transaction_id: b.port_charges_transaction_id,
                levy_transaction_id: b.levy_transaction_id,
                procurement_documents: b.procurement_documents ?? [],
                tax_transaction: b.tax_transaction ?? null,
                port_charges_transaction: b.port_charges_transaction ?? null,
                levy_transaction: b.levy_transaction ?? null,
                items,
            };
        },
        enabled: enabled && memberIds.length > 0,
    });
}

// ─── Sheet ──────────────────────────────────────────────────────────────────

interface ContainerCustomsSheetProps {
    /** null when closed — the sheet reads container display fields (title, fullMembers, fullSize,
     * confirmedBl) straight off the tile's `ContainerGroup`, no separate fetch needed. */
    container: ContainerGroup | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function buildSheetTitle(container: ContainerGroup): string {
    const base = [container.shipName, container.voyageNo ? `V.${container.voyageNo}` : null].filter(Boolean).join(' · ');
    return container.containerSuffix ? `${base}${container.containerSuffix}` : base || 'Container';
}

export default function ContainerCustomsSheet({ container, open, onOpenChange }: ContainerCustomsSheetProps) {
    const [tab, setTab] = useState<'documents' | 'payments'>('documents');

    // Keyed by sorted member vehicle ids, never the derived cluster key (see module doc above).
    const memberIds = useMemo(
        () => container ? [...new Set(container.fullMembers.map(m => m.id))].sort((a, b) => a - b) : [],
        [container],
    );

    const { data: boe, isLoading: boeLoading } = useContainerBoe(memberIds, open);

    // Reset to the Documents tab whenever a different container is opened.
    useEffect(() => {
        if (open) setTab('documents');
    }, [open, container?.key]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[640px] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
            >
                <SheetHeader>
                    <SheetTitle>Customs — {container ? buildSheetTitle(container) : ''}</SheetTitle>
                </SheetHeader>

                {container && (
                    <Tabs
                        value={tab}
                        onValueChange={(v) => setTab(v as 'documents' | 'payments')}
                        className="flex-1 flex flex-col min-h-0 mt-4"
                    >
                        <TabsList className="self-start shrink-0">
                            <TabsTrigger value="documents">Documents</TabsTrigger>
                            <TabsTrigger value="payments" disabled={!boe} title={!boe ? 'Upload the Bill of Entry first' : undefined}>
                                Payments
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="documents" className="flex-1 overflow-y-auto mt-4">
                            <CustomsDocumentsPanel
                                fullMembers={container.fullMembers}
                                fullSize={container.fullSize}
                                confirmedBl={container.confirmedBl}
                                boe={boe ?? null}
                                isLoading={boeLoading}
                            />
                        </TabsContent>

                        <TabsContent value="payments" className="flex-1 overflow-y-auto mt-4">
                            {boe ? (
                                <CustomsPaymentsPanel
                                    memberIds={memberIds}
                                    fullMembers={container.fullMembers}
                                    boe={boe}
                                />
                            ) : (
                                <div className="py-12 text-center text-sm text-muted-foreground">
                                    {boeLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Upload the Bill of Entry first'}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                )}
            </SheetContent>
        </Sheet>
    );
}
